import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

export const VISIBILITY_MODES = [
  {
    key: 'admin',
    label: 'Admin',
    eyebrow: 'OPERATIONAL CONTROL',
    heading: 'Active candidate control room',
    description: 'Full operational receipts, verification states, hashes, failures, and current-source provenance. Historical audit evidence remains preserved outside current-law navigation.',
  },
  {
    key: 'professional',
    label: 'Professional',
    eyebrow: 'PROFESSIONAL REVIEW',
    heading: 'Verified legal decomposition',
    description: 'Source-bound legal structure, verification, certification, and provenance without deployment-control noise.',
  },
  {
    key: 'advocate',
    label: 'Advocate',
    eyebrow: 'ADVOCATE VIEW',
    heading: 'What the law does and where attention is needed',
    description: 'Focuses on supported findings, unresolved gaps, exceptions, responsibilities, and the parts that require human attention.',
  },
  {
    key: 'guided',
    label: 'Guided',
    eyebrow: 'GUIDED VIEW',
    heading: 'Understand this Rosetta result step by step',
    description: 'Plain-language explanations of what passed, what failed, what each layer means, and why a result is or is not ready.',
  },
];

export const VISIBILITY_MODE_MAP = Object.fromEntries(
  VISIBILITY_MODES.map((mode) => [mode.key, mode]),
);

export function initialVisibilityMode() {
  if (typeof window === 'undefined') return 'admin';
  const saved = window.localStorage.getItem('rosetta-v2540-visibility-mode');
  return VISIBILITY_MODE_MAP[saved] ? saved : 'admin';
}

const VisibilityModeContext = createContext(null);

export function VisibilityModeProvider({ children }) {
  const [mode, setModeState] = useState(initialVisibilityMode);

  const setMode = (nextMode) => {
    const safeMode = VISIBILITY_MODE_MAP[nextMode] ? nextMode : 'admin';
    setModeState(safeMode);
  };

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem('rosetta-v2540-visibility-mode', mode);
    }
  }, [mode]);

  const value = useMemo(() => ({
    mode,
    setMode,
    modeDefinition: VISIBILITY_MODE_MAP[mode] || VISIBILITY_MODE_MAP.admin,
  }), [mode]);

  return (
    <VisibilityModeContext.Provider value={value}>
      {children}
    </VisibilityModeContext.Provider>
  );
}

export function useVisibilityMode() {
  const value = useContext(VisibilityModeContext);
  if (!value) {
    throw new Error('useVisibilityMode must be used inside VisibilityModeProvider');
  }
  return value;
}

export function VisibilityModeSelector({ mode, onChange, compact = false }) {
  const context = useContext(VisibilityModeContext);
  const activeMode = mode || context?.mode || 'admin';
  const changeMode = onChange || context?.setMode || (() => {});
  const definition = VISIBILITY_MODE_MAP[activeMode] || VISIBILITY_MODE_MAP.admin;

  if (compact) {
    return (
      <section className="panel visibility-mode-bar">
        <div className="section-heading">
          <div>
            <span className="eyebrow">ROSETTA VIEW</span>
            <h2>Same truth. Choose the level of explanation.</h2>
          </div>
        </div>
        <div className="page-actions" style={{ justifyContent: 'flex-start', flexWrap: 'wrap' }}>
          {VISIBILITY_MODES.map((item) => (
            <button
              key={item.key}
              className={activeMode === item.key ? 'button button-primary' : 'button'}
              type="button"
              aria-pressed={activeMode === item.key}
              onClick={() => changeMode(item.key)}
            >
              {item.label}
            </button>
          ))}
        </div>
        <p>{definition.description}</p>
      </section>
    );
  }

  return (
    <section className="panel" style={{ marginTop: 20 }}>
      <div className="section-heading">
        <div>
          <span className="eyebrow">LAYERED VISIBILITY</span>
          <h2>Same Rosetta truth. Different level of explanation.</h2>
        </div>
      </div>
      <div className="page-actions" style={{ justifyContent: 'flex-start', flexWrap: 'wrap' }}>
        {VISIBILITY_MODES.map((item) => (
          <button
            key={item.key}
            className={activeMode === item.key ? 'button button-primary' : 'button'}
            type="button"
            aria-pressed={activeMode === item.key}
            onClick={() => changeMode(item.key)}
          >
            {item.label}
          </button>
        ))}
      </div>
      <p>{definition.description}</p>
    </section>
  );
}
